/**
 * AdminContent (/admin) 페이지 번역 모듈 (#395 Stage 4d)
 *
 * loginPage.ts 패턴 정합. 인라인 `language === 'es' ? ... : English` 조건문 제거 후
 * 단일 진입점으로 통합. ko/es/en 3 언어 모두 정의 강제.
 *
 * es-MX 번역은 멕시코 정식 서비스 오픈 정합 (악센트 / 멕시코 표준 어휘).
 */
import type { Language, Locale } from '@/config/locales';

export interface AdminPageTranslation {
  myProfile: string;
  myOrders: string;
  partnerVisibilitySettings: string;
  languageSettings: string;
  paymentMethod: string;
  taxInvoice: string;
  announcements: string;
  faqAndSupport: string;
  termsOfService: string;
  privacyPolicy: string;
  opensInNewTab: string;
  deleteAccount: string;
  logIn: string;
  logOut: string;
}

// #512 — Locale 단일 출처 정합.
export type AdminPageTranslations =
  & Record<Language, AdminPageTranslation>
  & Record<Exclude<Locale, Language>, AdminPageTranslation>;

export const adminPageTranslations: AdminPageTranslations = {
  ko: {
    myProfile: '내 프로필',
    myOrders: '내 주문',
    partnerVisibilitySettings: '개인정보 설정',
    languageSettings: '언어 설정',
    paymentMethod: '결제 수단',
    taxInvoice: '세금계산서',
    announcements: '공지사항',
    faqAndSupport: '예상 질문 답변 CS',
    termsOfService: '약관',
    privacyPolicy: '개인정보보호정책',
    opensInNewTab: '새 탭에서 열림',
    deleteAccount: '탈퇴',
    logIn: '로그인',
    logOut: '로그아웃',
  },
  es: {
    myProfile: 'Mi perfil',
    myOrders: 'Mis pedidos',
    partnerVisibilitySettings: 'Configuración de privacidad',
    languageSettings: 'Configuración de idioma',
    paymentMethod: 'Método de pago',
    taxInvoice: 'Factura fiscal',
    announcements: 'Anuncios',
    faqAndSupport: 'Preguntas frecuentes y soporte',
    termsOfService: 'Términos del Servicio',
    privacyPolicy: 'Política de Privacidad',
    opensInNewTab: 'se abre en una pestaña nueva',
    deleteAccount: 'Eliminar cuenta',
    logIn: 'Iniciar sesión',
    logOut: 'Cerrar sesión',
  },
  en: {
    myProfile: 'My profile',
    myOrders: 'My Orders',
    partnerVisibilitySettings: 'Privacy settings',
    languageSettings: 'Language Settings',
    paymentMethod: 'Payment method',
    taxInvoice: 'Tax Invoice',
    announcements: 'Announcements',
    faqAndSupport: 'FAQ & Support',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    opensInNewTab: 'opens in a new tab',
    deleteAccount: 'Delete Account',
    logIn: 'Log in',
    logOut: 'Log out',
  },
  pt: {
    myProfile: 'Meu perfil',
    myOrders: 'Meus pedidos',
    partnerVisibilitySettings: 'Configurações de privacidade',
    languageSettings: 'Configurações de idioma',
    paymentMethod: 'Método de pagamento',
    taxInvoice: 'Nota fiscal',
    announcements: 'Anúncios',
    faqAndSupport: 'Perguntas frequentes e suporte',
    termsOfService: 'Termos de serviço',
    privacyPolicy: 'Política de privacidade',
    opensInNewTab: 'abre em uma nova aba',
    deleteAccount: 'Excluir conta',
    logIn: 'Entrar',
    logOut: 'Sair',
  },
  fr: {
    myProfile: 'Mon profil',
    myOrders: 'Mes commandes',
    partnerVisibilitySettings: 'Paramètres de confidentialité',
    languageSettings: 'Paramètres de langue',
    paymentMethod: 'Mode de paiement',
    taxInvoice: 'Facture fiscale',
    announcements: 'Annonces',
    faqAndSupport: 'FAQ et assistance',
    termsOfService: 'Conditions d’utilisation',
    privacyPolicy: 'Politique de confidentialité',
    opensInNewTab: 's’ouvre dans un nouvel onglet',
    deleteAccount: 'Supprimer le compte',
    logIn: 'Se connecter',
    logOut: 'Se déconnecter',
  },
  zh: {
    myProfile: '我的资料',
    myOrders: '我的订单',
    partnerVisibilitySettings: '隐私设置',
    languageSettings: '语言设置',
    paymentMethod: '支付方式',
    taxInvoice: '税务发票',
    announcements: '公告',
    faqAndSupport: '常见问题与支持',
    termsOfService: '服务条款',
    privacyPolicy: '隐私政策',
    opensInNewTab: '在新标签页中打开',
    deleteAccount: '删除账户',
    logIn: '登录',
    logOut: '退出登录',
  },
  ja: {
    myProfile: 'マイプロフィール',
    myOrders: 'マイオーダー',
    partnerVisibilitySettings: 'プライバシー設定',
    languageSettings: '言語設定',
    paymentMethod: '支払い方法',
    taxInvoice: '請求書',
    announcements: 'お知らせ',
    faqAndSupport: 'よくある質問とサポート',
    termsOfService: '利用規約',
    privacyPolicy: 'プライバシーポリシー',
    opensInNewTab: '新しいタブで開きます',
    deleteAccount: 'アカウント削除',
    logIn: 'ログイン',
    logOut: 'ログアウト',
  },
  hi: {
    myProfile: 'मेरी प्रोफ़ाइल',
    myOrders: 'मेरे ऑर्डर',
    partnerVisibilitySettings: 'गोपनीयता सेटिंग',
    languageSettings: 'भाषा सेटिंग्स',
    paymentMethod: 'भुगतान विधि',
    taxInvoice: 'कर चालान',
    announcements: 'घोषणाएं',
    faqAndSupport: 'सामान्य प्रश्न और सहायता',
    termsOfService: 'सेवा की शर्तें',
    privacyPolicy: 'गोपनीयता नीति',
    opensInNewTab: 'नए टैब में खुलेगा',
    deleteAccount: 'खाता हटाएं',
    logIn: 'लॉग इन',
    logOut: 'लॉग आउट',
  },
  de: {
    myProfile: 'Mein Profil',
    myOrders: 'Meine Bestellungen',
    partnerVisibilitySettings: 'Datenschutzeinstellungen',
    languageSettings: 'Spracheinstellungen',
    paymentMethod: 'Zahlungsmethode',
    taxInvoice: 'Steuerrechnung',
    announcements: 'Ankündigungen',
    faqAndSupport: 'FAQ und Support',
    termsOfService: 'Nutzungsbedingungen',
    privacyPolicy: 'Datenschutzrichtlinie',
    opensInNewTab: 'öffnet in einem neuen Tab',
    deleteAccount: 'Konto löschen',
    logIn: 'Anmelden',
    logOut: 'Abmelden',
  },
  it: {
    myProfile: 'Il mio profilo',
    myOrders: 'I miei ordini',
    partnerVisibilitySettings: 'Impostazioni privacy',
    languageSettings: 'Impostazioni lingua',
    paymentMethod: 'Metodo di pagamento',
    taxInvoice: 'Fattura fiscale',
    announcements: 'Annunci',
    faqAndSupport: 'FAQ e assistenza',
    termsOfService: 'Termini di servizio',
    privacyPolicy: 'Informativa sulla privacy',
    opensInNewTab: 'si apre in una nuova scheda',
    deleteAccount: 'Elimina account',
    logIn: 'Accedi',
    logOut: 'Esci',
  },
  ru: {
    myProfile: 'Мой профиль',
    myOrders: 'Мои заказы',
    partnerVisibilitySettings: 'Настройки конфиденциальности',
    languageSettings: 'Настройки языка',
    paymentMethod: 'Способ оплаты',
    taxInvoice: 'Налоговая накладная',
    announcements: 'Объявления',
    faqAndSupport: 'Часто задаваемые вопросы и поддержка',
    termsOfService: 'Условия использования',
    privacyPolicy: 'Политика конфиденциальности',
    opensInNewTab: 'откроется в новой вкладке',
    deleteAccount: 'Удалить аккаунт',
    logIn: 'Войти',
    logOut: 'Выйти',
  },
  ar: {
    myProfile: 'ملفي الشخصي',
    myOrders: 'طلباتي',
    partnerVisibilitySettings: 'إعدادات الخصوصية',
    languageSettings: 'إعدادات اللغة',
    paymentMethod: 'طريقة الدفع',
    taxInvoice: 'فاتورة ضريبية',
    announcements: 'الإعلانات',
    faqAndSupport: 'الأسئلة الشائعة والدعم',
    termsOfService: 'شروط الخدمة',
    privacyPolicy: 'سياسة الخصوصية',
    opensInNewTab: 'يفتح في تبويب جديد',
    deleteAccount: 'حذف الحساب',
    logIn: 'تسجيل الدخول',
    logOut: 'تسجيل الخروج',
  },
  vi: {
    myProfile: 'Hồ sơ của tôi',
    myOrders: 'Đơn hàng của tôi',
    partnerVisibilitySettings: 'Cài đặt quyền riêng tư',
    languageSettings: 'Cài đặt ngôn ngữ',
    paymentMethod: 'Phương thức thanh toán',
    taxInvoice: 'Hóa đơn thuế',
    announcements: 'Thông báo',
    faqAndSupport: 'Câu hỏi thường gặp và hỗ trợ',
    termsOfService: 'Điều khoản dịch vụ',
    privacyPolicy: 'Chính sách bảo mật',
    opensInNewTab: 'mở trong tab mới',
    deleteAccount: 'Xóa tài khoản',
    logIn: 'Đăng nhập',
    logOut: 'Đăng xuất',
  },
  th: {
    myProfile: 'โปรไฟล์ของฉัน',
    myOrders: 'คำสั่งซื้อของฉัน',
    partnerVisibilitySettings: 'การตั้งค่าความเป็นส่วนตัว',
    languageSettings: 'การตั้งค่าภาษา',
    paymentMethod: 'วิธีการชำระเงิน',
    taxInvoice: 'ใบกำกับภาษี',
    announcements: 'ประกาศ',
    faqAndSupport: 'คำถามที่พบบ่อยและการสนับสนุน',
    termsOfService: 'ข้อกำหนดการให้บริการ',
    privacyPolicy: 'นโยบายความเป็นส่วนตัว',
    opensInNewTab: 'เปิดในแท็บใหม่',
    deleteAccount: 'ลบบัญชี',
    logIn: 'เข้าสู่ระบบ',
    logOut: 'ออกจากระบบ',
  },
  nl: {
    myProfile: 'Mijn profiel',
    myOrders: 'Mijn bestellingen',
    partnerVisibilitySettings: 'Privacyinstellingen',
    languageSettings: 'Taalinstellingen',
    paymentMethod: 'Betaalmethode',
    taxInvoice: 'Belastingfactuur',
    announcements: 'Aankondigingen',
    faqAndSupport: 'Veelgestelde vragen en ondersteuning',
    termsOfService: 'Servicevoorwaarden',
    privacyPolicy: 'Privacybeleid',
    opensInNewTab: 'opent in een nieuw tabblad',
    deleteAccount: 'Account verwijderen',
    logIn: 'Inloggen',
    logOut: 'Uitloggen',
  },
};

/**
 * 현재 locale 의 번역 객체 반환. fallback: en.
 */
export function getAdminPageTranslation(language: Locale): AdminPageTranslation {
  return adminPageTranslations[language] ?? adminPageTranslations.en;
}
